import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  useTheme,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import AddStreamDetails from "./steps/AddStreamDetails";
import AddStreamGraphicsMedia from "./steps/AddStreamGraphicsMedia";
import StreamPackageSelection from "./steps/StreamPackageSelection";
import { tokens } from "../../theme";
import TeamAssignment from "./steps/AddStreamTeamAssignment";
import apiService from "../../service/Api";
import { toast } from "react-toastify";
import { checkPermission } from "../../service/permissionGuard";
import { PERMISSIONS } from "../../constants/permissions";
import { Venue } from "../../types/venue";
import { IStreamFormData } from "../../types/streams";
import { appendStreamPackageItem } from "./utils/stream-package-form-data";
import { useCallback, useEffect, useState } from "react";

interface StreamService {
  id: number;
  venue: Venue;
  type_of_service: string;
  start_time: string;
  end_time: string;
  date_of_service: string;
  special_request: string;
  service_notes: string;
  preacher_name: string;
  mc_name: string;
  eulogist_name: string;
}

interface StreamResponse {
  id: number;
  booking?: {
    id?: number | null;
  };
  service: StreamService | null;
  package_item?: {
    id: number;
    name: string;
    price: string | number;
    description?: string | null;
    package?: {
      id: number;
      name: string;
    } | number | null;
    services?: Array<{
      id: number;
      name: string;
    }>;
  } | null;
  title: string;
  start: string;
  end: string;
  date: string;
  status: string;
  youtube_stream_link: string;
  website_stream_link: string;
  go_fund_me_link: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
}

interface BookingData {
  booking_type?: string;
  service?: string;
  events?: Array<{
    id?: number;
    venue?: { id?: number };
    title?: string;
    start_time?: string;
    end_time?: string;
    start_date_of_event?: string;
    special_requiest?: string;
    special_notes?: string;
    notes?: string;
  }>;
  services?: Array<{
    id?: number;
    venue?: { id?: number };
    type_of_service?: string;
    start_time?: string;
    end_time?: string;
    date_of_service?: string;
    special_request?: string;
    service_notes?: string;
    preacher_name?: string;
    mc_name?: string;
    eulogist_name?: string;
  }>;
}

const appendFormValue = (
  formData: FormData,
  key: string,
  value: unknown
) => {
  if (value === undefined || value === null) return;
  formData.append(key, String(value));
};

interface TimeAllocation {
  id: number;
  stream: number;
  call_time: string;
  departure_time: string;
  arrival_at_venue_time: string;
  setup_from_time: string;
  setup_to_time: string;
  shoot_start_time: string;
  shoot_end_time: string;
  estimated_wrap_time: string;
  back_to_base_time: string;
  unpacking_time: string;
  depart_time: string;
  created_at: string;
  updated_at: string;
  created_by: number | null;
  last_updated_by: number | null;
}

const EditStream: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const streamId = id ? parseInt(id, 10) : null;
  const [activeTab, setActiveTab] = useState(0);
  const [bookingType, setBookingType] = useState<string>("");
  const [formData, setFormData] = useState<IStreamFormData>({
    stream_id: streamId,
    service_id: null,
    booking: null,
    title: "",
    serviceType: "",
    startTime: "",
    endTime: "",
    date: "",
    youtube_stream_link: "",
    go_fund_me_link: "",
    website_stream_link: "",
    venue: null,
    specialNotes: "",
    travelDuration: null,
    callTime: "",
    travellingFrom: "",
    travellingTo: "",
    setupFrom: "",
    setupTo: "",
    estimatedWrap: "",
    priest_name: "",
    mc_name: "",
    eulogist_name: "",
    other: "",
    crew: { members: [] },
    call_sheets: [],
    thumbnails: [],
    slideshows: [],
    programs: [],
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const fetchStreamData = async () => {
      if (!streamId) {
        toast.error("Invalid stream ID.");
        setFetching(false);
        return;
      }
      try {
        const streamResponse = await apiService.get<StreamResponse>(
          `/api/streams/stream/${streamId}/`
        );
        const rawStream = streamResponse.data;

        const stream: StreamResponse = {
          ...rawStream,
          booking: {
            id: rawStream.booking?.id ?? null,
          },
          service: rawStream.service ?? null,
        };

        if (!stream.booking?.id) {
          throw new Error("Missing booking ID for stream.");
        }

        const bookingResponse = await apiService.get<BookingData>(
          `/api/bookings/booking/${stream.booking.id}/`
        );
        const bookingData = bookingResponse.data;

        const timeResponse = await apiService.get<TimeAllocation>(
          `/api/streams/time-algorithim/?stream_id=${streamId}`
        );
        const timeData = timeResponse.data;

        let travelDuration: number | null = null;
        if (timeData.departure_time && timeData.arrival_at_venue_time) {
          const fromTime = new Date(`1970-01-01T${timeData.departure_time}:00`);
          const toTime = new Date(
            `1970-01-01T${timeData.arrival_at_venue_time}:00`
          );
          let diffInMinutes =
            (toTime.getTime() - fromTime.getTime()) / (1000 * 60);
          if (diffInMinutes < 0) {
            diffInMinutes += 24 * 60;
          }
          travelDuration = Math.round(diffInMinutes);
        }

        const bookingTypeValue = bookingData.booking_type || "";
        setBookingType(bookingTypeValue);
        const isConferenceOrEvent =
          bookingTypeValue === "CONFERENCE" || bookingTypeValue === "EVENT";

        let serviceData: {
          type_of_service?: string;
          start_time?: string;
          end_time?: string;
          date_of_service?: string;
          special_request?: string;
          service_notes?: string;
          preacher_name?: string;
          mc_name?: string;
          eulogist_name?: string;
        } = {};
        let venueId: number | null = null;
        let serviceId: number | null = null;

        if (isConferenceOrEvent) {
          const eventData = bookingData.events?.[0];
          if (eventData) {
            serviceData = {
              type_of_service: bookingData.service || eventData.title || "",
              start_time: eventData.start_time || stream.start || "",
              end_time: eventData.end_time || stream.end || "",
              date_of_service:
                eventData.start_date_of_event || stream.date || "",
              special_request:
                eventData.special_requiest || eventData.special_notes || "",
              service_notes: eventData.notes || "",
              preacher_name: "",
              mc_name: "",
              eulogist_name: "",
            };
            venueId = eventData.venue?.id || null;
            serviceId = eventData.id || null;
          }
        } else {
          const serviceFromBooking = bookingData.services?.[0];
          if (serviceFromBooking) {
            serviceData = {
              type_of_service: serviceFromBooking.type_of_service || "",
              start_time: serviceFromBooking.start_time || stream.start || "",
              end_time: serviceFromBooking.end_time || stream.end || "",
              date_of_service:
                serviceFromBooking.date_of_service || stream.date || "",
              special_request: serviceFromBooking.special_request || "",
              service_notes: serviceFromBooking.service_notes || "",
              preacher_name: serviceFromBooking.preacher_name || "",
              mc_name: serviceFromBooking.mc_name || "",
              eulogist_name: serviceFromBooking.eulogist_name || "",
            };
            venueId = serviceFromBooking.venue?.id || null;
            serviceId = serviceFromBooking.id || null;
          }
        }

        const service = stream.service as StreamService;
        if (service) {
          serviceData = {
            type_of_service:
              service.type_of_service || serviceData.type_of_service || "",
            start_time: service.start_time || serviceData.start_time || "",
            end_time: service.end_time || serviceData.end_time || "",
            date_of_service:
              service.date_of_service || serviceData.date_of_service || "",
            special_request:
              service.special_request || serviceData.special_request || "",
            service_notes:
              service.service_notes || serviceData.service_notes || "",
            preacher_name:
              service.preacher_name || serviceData.preacher_name || "",
            mc_name: service.mc_name || serviceData.mc_name || "",
            eulogist_name:
              service.eulogist_name || serviceData.eulogist_name || "",
          };
          venueId = service.venue?.id || venueId;
          serviceId = service.id || serviceId;
        }

        if (!venueId) {
          console.warn(
            "No venue found in service or booking data. Using default values."
          );
        }

        setFormData({
          stream_id: stream.id,
          service_id: serviceId,
          booking: stream.booking?.id ?? null,
          title: stream.title || "",
          youtube_stream_link: stream.youtube_stream_link || "",
          go_fund_me_link: stream.go_fund_me_link || "",
          website_stream_link: stream.website_stream_link || "",
          serviceType: serviceData.type_of_service || "",
          startTime: timeData.shoot_start_time || serviceData.start_time || "",
          endTime: timeData.shoot_end_time || serviceData.end_time || "",
          date: serviceData.date_of_service || "",
          venue: venueId,
          specialNotes: serviceData.special_request || "",
          travelDuration: travelDuration || null,
          callTime: timeData.call_time || "",

          travellingFrom: timeData.departure_time || "",
          travellingTo: timeData.arrival_at_venue_time || "",
          setupFrom: timeData.setup_from_time || "",
          setupTo: timeData.setup_to_time || "",
          estimatedWrap: timeData.estimated_wrap_time || "",
          priest_name: serviceData.preacher_name || "",
          mc_name: serviceData.mc_name || "",
          eulogist_name: serviceData.eulogist_name || "",
          other: serviceData.service_notes || "",
          // Prefill the current package snapshot so the Packages & Services tab
          // reflects what this stream already has. `package` holds the parent
          // Package id (to match the fetched package list); the snapshot's
          // services become the selected service ids.
          package: stream.package_item?.package
            ? {
                id:
                  typeof stream.package_item.package === "number"
                    ? stream.package_item.package
                    : stream.package_item.package.id,
                name: stream.package_item.name,
                price: Number(stream.package_item.price) || 0,
                contents: stream.package_item.description
                  ? [stream.package_item.description]
                  : [],
                base_services: [],
              }
            : null,
          package_service_ids:
            stream.package_item?.services?.map((service) => service.id) ?? [],
          crew: { members: [] },
          call_sheets: [],
          thumbnails: [],
          slideshows: [],
          programs: [],
        });
      } catch (err: unknown) {
        console.error("Failed to fetch stream or time allocation:", err);
        const errorMessage =
          (err as { message?: string })?.message ||
          "Failed to load stream data or time allocation. Please try again.";
        toast.error(errorMessage);
      } finally {
        setFetching(false);
      }
    };

    fetchStreamData();
  }, [streamId]);

  const handleInputChange = useCallback(
    (field: keyof IStreamFormData, value: string) => {
      let val: number | string | null | undefined = value;
      if (["booking", "venue", "stream_id", "service_id"].includes(field)) {
        val = value ? parseInt(value, 10) : null;
      } else if (field === "travelDuration") {
        val = value ? parseInt(value, 10) : null;
      } else if (field === "title") {
        val = value || undefined;
      }
      setFormData((prev) => ({ ...prev, [field]: val }));
    },
    []
  );

  const handleThumbnailChange = (files: File[]) =>
    setFormData((prev) => ({ ...prev, thumbnails: files }));
  const handleSlideshowChange = (files: File[]) =>
    setFormData((prev) => ({ ...prev, slideshows: files }));
  const handleProgramChange = (files: File[]) =>
    setFormData((prev) => ({ ...prev, programs: files }));

  const handleSubmit = async () => {
    if (
      !formData.booking ||
      !formData.venue ||
      !formData.service_id ||
      !formData.serviceType ||
      !formData.startTime ||
      !formData.endTime ||
      !formData.date ||
      !formData.title
    ) {
      toast.error(
        "Please fill in all required fields (Title, Booking, Venue, Service Type, Start Time, End Time, Date)."
      );
      return;
    }
    if (!formData.stream_id) {
      toast.error("Stream ID is missing.");
      return;
    }
    setLoading(true);
    try {
      checkPermission(PERMISSIONS.STREAM_CHANGE);
      const isConferenceOrEvent =
        bookingType === "CONFERENCE" || bookingType === "EVENT";

      const formDataToSend = new FormData();

      if (isConferenceOrEvent) {
        const eventData: Record<string, unknown> = {
          event_id: formData.service_id
            ? formData.service_id.toString()
            : undefined,
          venue: formData.venue ? formData.venue.toString() : undefined,
          title: formData.title || formData.serviceType || undefined,
          start_date_of_event: formData.date || undefined,
          end_date_of_event: formData.date || undefined,
          start_time: formData.startTime || undefined,
          end_time: formData.endTime || undefined,
          special_notes: formData.specialNotes || undefined,
          notes: formData.other || undefined,
        };
        Object.entries(eventData).forEach(([key, value]) => {
          appendFormValue(formDataToSend, `event[${key}]`, value);
        });
      } else {
        const serviceData: Record<string, unknown> = {
          service_id: formData.service_id
            ? formData.service_id.toString()
            : undefined,
          venue: formData.venue ? formData.venue.toString() : undefined,
          type_of_service: formData.serviceType || undefined,
          start_time: formData.startTime || undefined,
          end_time: formData.endTime || undefined,
          date_of_service: formData.date || undefined,
          special_request: formData.specialNotes || undefined,
          service_notes: formData.other || undefined,
          preacher_name: formData.priest_name || undefined,
          mc_name: formData.mc_name || undefined,
          eulogist_name: formData.eulogist_name || undefined,
        };
        Object.entries(serviceData).forEach(([key, value]) => {
          appendFormValue(formDataToSend, `service[${key}]`, value);
        });
      }

      const streamData: Record<string, unknown> = {
        stream_id: formData.stream_id.toString(),
        title: formData.title || undefined,
        youtube_stream_link: formData.youtube_stream_link || undefined,
        go_fund_me_link: formData.go_fund_me_link || undefined,
        website_stream_link: formData.website_stream_link || undefined,
        start: formData.startTime || undefined,
        end: formData.endTime || undefined,
        date: formData.date || undefined,
      };
      Object.entries(streamData).forEach(([key, value]) => {
        appendFormValue(formDataToSend, `stream[${key}]`, value);
      });

      // Per-stream package override. A package chosen in the Packages &
      // Services tab is posted as a package_item[...] snapshot (built/cloned
      // server-side); a bare package_item_id just repoints at an existing item.
      if (formData.package) {
        appendStreamPackageItem(formDataToSend, formData);
      } else if (formData.package_item_id != null) {
        appendFormValue(
          formDataToSend,
          "package_item_id",
          formData.package_item_id
        );
      }

      const topLevelFields: {
        callTime?: string;
        travellingFrom?: string;
        travellingTo?: string;
        setupFrom?: string;
        setupTo?: string;
        estimatedWrap?: string;
        travelDuration?: number | null;
        crew?: IStreamFormData["crew"];
        call_sheets?: IStreamFormData["call_sheets"];
        thumbnails?: File[];
        slideshows?: File[];
        programs?: File[];
      } = {
        callTime: formData.callTime || undefined,
        travellingFrom: formData.travellingFrom || undefined,
        travellingTo: formData.travellingTo || undefined,
        setupFrom: formData.setupFrom || undefined,
        setupTo: formData.setupTo || undefined,
        estimatedWrap: formData.estimatedWrap || undefined,
        travelDuration: formData.travelDuration ?? undefined,
        crew: formData.crew,
        call_sheets: formData.call_sheets || [],
        thumbnails: formData.thumbnails || [],
        slideshows: formData.slideshows || [],
        programs: formData.programs || [],
      };

      Object.entries(topLevelFields).forEach(([key, value]) => {
        if (value === undefined) return;
        if (key === "crew") {
          formDataToSend.append(key, JSON.stringify(value));
        } else if (key === "call_sheets") {
          (value as IStreamFormData["call_sheets"]).forEach((sheet, index) => {
            formDataToSend.append(
              `call_sheets[${index}]`,
              JSON.stringify(sheet)
            );
          });
        } else if (["thumbnails", "slideshows", "programs"].includes(key)) {
          (value as File[]).forEach((file) => {
            formDataToSend.append(`${key}[]`, file);
          });
        } else {
          appendFormValue(formDataToSend, key, value);
        }
      });

      const formDataEntries: Record<string, unknown> = {};
      formDataToSend.forEach((value, key) => {
        formDataEntries[key] = value;
      });

      await apiService.put(
        `/api/streams/stream/${formData.stream_id}/full-update/`,
        formDataToSend,
        false
      );
      toast.success("Stream updated successfully");
      navigate(`/stream-management/view/${formData.stream_id}`, {
        replace: true,
      });
    } catch (err: unknown) {
      console.error("Failed to update stream:", err);
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to update stream. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = (tab: number) => {
    switch (tab) {
      case 0:
        return (
          <AddStreamDetails
            formData={formData}
            handleInputChange={handleInputChange}
            isEdit={true}
          />
        );
      case 1:
        return (
          <StreamPackageSelection
            formData={formData}
            setFormData={setFormData}
            bookingType={bookingType}
          />
        );
      case 2:
        return <TeamAssignment formData={formData} setFormData={setFormData} />;
      case 3:
        return (
          <AddStreamGraphicsMedia
            handleThumbnailChange={handleThumbnailChange}
            handleSlideshowChange={handleSlideshowChange}
            handleProgramChange={handleProgramChange}
          />
        );

      default:
        return null;
    }
  };

  if (fetching) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2, boxShadow: 0 }}>
        <Typography variant="h4" gutterBottom>
          Edit Stream
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2 }}
          >
            <Tab label="Stream Details" />
            <Tab label="Packages & Services" />
            <Tab label="Team Assignment" />
            <Tab label="Graphics & Media" />
          </Tabs>
          <Button
            sx={{
              backgroundColor: colors.primary[400],
              pr: 5,
              pl: 5,
              pt: 1,
              pb: 1,
              color: "white",
            }}
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Update Stream"
            )}
          </Button>
        </Box>

        {renderTabContent(activeTab)}
      </Paper>
    </Box>
  );
};

export default EditStream;
